import { useOnArrayChangeEffect } from "@mirage-web/utils/react-helpers";
// @oasis-permission-inherits: /ai-edits (studio-edits:view)
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, DownloadIcon, LinkIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { match } from "ts-pattern";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import { cn } from "~/lib/utils";
import { fetchAiEditLookupQuery, fetchAiEditsFeedQuery } from "~/modules/ai-edits/queries";
import {
  AiEdit,
  AiEditDetail,
  AiEditsFeedSearch,
  BboxAttemptArtifacts,
  EDIT_SURFACE_LABELS,
  EditSurface,
  VIDEO_TYPE_LABELS,
  VideoType,
  aiEditsFeedSearchSchema,
  videoTypeEnumSchema,
} from "~/modules/ai-edits/schemas";

function syntaxHighlight(json: string) {
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls = "text-orange-600";
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = "text-blue-700";
        } else {
          cls = "text-green-700";
        }
      } else if (/true|false/.test(match)) {
        cls = "text-purple-700";
      } else if (/null/.test(match)) {
        cls = "text-red-600";
      }
      return `<span class="${cls}">${match}</span>`;
    }
  );
}

async function downloadUrl(url: string, filename: string) {
  const res = await fetch(url);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(blobUrl);
}

function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function DownloadButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="p-1 rounded hover:bg-zinc-700 text-muted-foreground hover:text-white transition-colors"
      title="Download"
    >
      <DownloadIcon className="size-3" />
    </button>
  );
}

function ImageWithDownload({
  src,
  alt,
  className,
  label,
}: {
  src: string;
  alt: string;
  className?: string;
  label: string;
}) {
  const filename = label.toLowerCase().replace(/\s+/g, "-") + ".png";
  return (
    <div>
      <div className="flex items-center gap-1 mb-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <DownloadButton onClick={() => downloadUrl(src, filename)} />
      </div>
      <img src={src} alt={alt} className={className} />
    </div>
  );
}

function JsonViewer({ url, label }: { url: string; label: string }) {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["json-fetch", url],
    queryFn: async () => {
      const res = await fetch(url);
      return res.json();
    },
  });

  const formattedJson = data ? syntaxHighlight(JSON.stringify(data, null, 2)) : "";
  const filename = label.toLowerCase().replace(/\s+/g, "-") + ".json";

  return (
    <div className="text-xs">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-primary hover:underline"
        >
          {expanded ? (
            <ChevronDownIcon className="size-3" />
          ) : (
            <ChevronRightIcon className="size-3" />
          )}
          {label}
          {isLoading && <span className="text-muted-foreground ml-1">(loading...)</span>}
          {error && <span className="text-red-400 ml-1">(error)</span>}
        </button>
        {data && <DownloadButton onClick={() => downloadJson(data, filename)} />}
      </div>
      {expanded && (
        <div className="mt-1 max-h-80 overflow-auto rounded bg-zinc-100 border border-zinc-300 p-3 text-[11px] font-mono">
          {isLoading && <span className="text-zinc-500">Loading...</span>}
          {error && <span className="text-red-600">Error loading JSON</span>}
          {data && (
            <pre
              className="whitespace-pre-wrap text-zinc-800"
              dangerouslySetInnerHTML={{ __html: formattedJson }}
            />
          )}
        </div>
      )}
    </div>
  );
}

type FeedSearch = AiEditsFeedSearch;

type AppliedFilters = {
  operationIds?: string[];
  userIds?: string[];
  videoTypes?: VideoType[];
  surface?: EditSurface;
  orgIds?: string[];
};

function splitIds(value: string | undefined): string[] | undefined {
  const ids = (value ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
  return ids.length > 0 ? ids : undefined;
}

function parseVideoTypeParam(value: string | undefined): VideoType | "all" {
  if (!value || value === "all") {
    return "all";
  }
  const parsed = videoTypeEnumSchema.safeParse(value);
  return parsed.success ? parsed.data : "all";
}

function buildFilters(input: {
  operationIds?: string;
  userIds?: string;
  orgIds?: string;
  videoType: VideoType | "all";
  surface: EditSurface | "all";
}): AppliedFilters {
  const filters: AppliedFilters = {};
  const operationIds = splitIds(input.operationIds);
  const userIds = splitIds(input.userIds);
  const orgIds = splitIds(input.orgIds);
  if (operationIds) {
    filters.operationIds = operationIds;
  }
  if (userIds) {
    filters.userIds = userIds;
  }
  if (orgIds) {
    filters.orgIds = orgIds;
  }
  if (input.videoType !== "all") {
    filters.videoTypes = [input.videoType];
  }
  if (input.surface !== "all") {
    filters.surface = input.surface;
  }
  return filters;
}

function filtersFromSearch(search: FeedSearch): AppliedFilters {
  return buildFilters({
    operationIds: search.operationIds,
    userIds: search.userIds,
    orgIds: search.orgIds,
    videoType: parseVideoTypeParam(search.videoTypes),
    surface: search.surface ?? "all",
  });
}

const SURFACE_OPTIONS: { value: EditSurface | "all"; label: string }[] = [
  { value: "all", label: "All Surfaces" },
  { value: "app", label: EDIT_SURFACE_LABELS.app },
  { value: "website", label: EDIT_SURFACE_LABELS.website },
];

const SURFACE_COLORS: Record<EditSurface, string> = {
  app: "bg-[#2F6F4E]",
  website: "bg-[#8A5A00]",
};

function SurfaceBadge({ edit }: { edit: Pick<AiEdit, "surface"> }) {
  if (!edit.surface) {
    return null;
  }
  return (
    <Badge className={`text-[10px] px-1.5 py-0 ${SURFACE_COLORS[edit.surface]} text-white`}>
      {EDIT_SURFACE_LABELS[edit.surface]}
    </Badge>
  );
}

/** Website edits link to the Mirage Studio video they were applied to. */
function StudioVideoLink({ edit }: { edit: Pick<AiEdit, "studioVideo"> }) {
  const search = Route.useSearch();
  if (!edit.studioVideo) {
    return null;
  }
  return (
    <Link
      to="/studio-edits/view/$videoId"
      params={{ videoId: edit.studioVideo.videoId }}
      search={search}
      className="text-primary hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      Studio video
    </Link>
  );
}

export const Route = createFileRoute("/_dash/ai-edits/all")({
  component: RouteComponent,
  validateSearch: aiEditsFeedSearchSchema,
});

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

type VideoDimensions = { width: number; height: number };

function isLandscape(dimensions: VideoDimensions | null): dimensions is VideoDimensions {
  return dimensions !== null && dimensions.width > dimensions.height;
}

const FRAME_VARIANTS = {
  card: {
    landscape: { frameWidth: "w-[280px]", timerPosition: "top-2 left-2" },
    portrait: { frameWidth: "w-[180px]", timerPosition: "top-5 left-2" },
    timerSize: "text-[10px] px-1.5 py-0.5",
    placeholderSize: "text-xs",
  },
  detail: {
    landscape: { frameWidth: "w-[380px]", timerPosition: "top-2 left-2" },
    portrait: { frameWidth: "w-[220px]", timerPosition: "top-8 left-3" },
    timerSize: "text-xs px-2 py-1",
    placeholderSize: "text-sm",
  },
} as const;

/**
 * Device mock-up frame around one edit's rendered video, with an elapsed/total overlay.
 *
 * The frame's own geometry and the surrounding layout both depend on the video's orientation, which
 * is only known once metadata loads, so the caller owns `dimensions` and the frame reports measured
 * dimensions back through `onDimensionsLoaded`.
 */
function EditVideoFrame({
  src,
  variant,
  dimensions,
  onDimensionsLoaded,
}: {
  src: string | null | undefined;
  variant: keyof typeof FRAME_VARIANTS;
  dimensions: VideoDimensions | null;
  onDimensionsLoaded: (dimensions: VideoDimensions) => void;
}) {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const { landscape, portrait, timerSize, placeholderSize } = FRAME_VARIANTS[variant];
  const aspectRatio = isLandscape(dimensions) ? dimensions.width / dimensions.height : null;
  const { frameWidth, timerPosition } = aspectRatio === null ? portrait : landscape;

  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = e.currentTarget;
    setDuration(video.duration);
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (width && height) {
      onDimensionsLoaded({ width, height });
    }
  };

  const screen =
    src && src.length > 0 ? (
      <>
        <video
          src={src}
          controls
          className="w-full h-full object-contain"
          preload="metadata"
          onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
          onLoadedMetadata={handleLoadedMetadata}
        />
        {duration > 0 && (
          <div
            className={cn(
              "absolute",
              timerPosition,
              "bg-black/70 text-white",
              timerSize,
              "rounded font-mono pointer-events-none"
            )}
          >
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        )}
      </>
    ) : (
      <span className={cn(placeholderSize, "text-zinc-500")}>No video</span>
    );

  if (aspectRatio !== null) {
    return (
      <div
        className={cn(
          "relative",
          frameWidth,
          "rounded-lg border-[4px] border-zinc-800 bg-zinc-800 shadow-xl overflow-hidden"
        )}
      >
        <div
          className="relative overflow-hidden bg-zinc-900 flex items-center justify-center"
          style={{ aspectRatio: `${aspectRatio}` }}
        >
          {screen}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative",
        frameWidth,
        "rounded-[2rem] border-[8px] border-zinc-800 bg-zinc-800 shadow-xl overflow-hidden"
      )}
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-4 bg-zinc-800 rounded-b-xl z-10" />
      <div className="aspect-[9/19.5] relative overflow-hidden rounded-[1.5rem] bg-zinc-900 flex items-center justify-center">
        {screen}
      </div>
    </div>
  );
}

/** Copies the permalink that filters this page down to a single edit. */
function useCopyEditLink(operationId: string) {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    const params = new URLSearchParams();
    params.set("operationIds", operationId);
    const url = `${window.location.origin}/ai-edits/all?${params.toString()}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return { copied, copyLink };
}

function EditCard({
  edit,
  onDimensionsLoaded,
}: {
  edit: AiEdit;
  onDimensionsLoaded?: (editId: string, width: number, height: number) => void;
}) {
  const navigate = useNavigate();
  const { copied, copyLink } = useCopyEditLink(edit.operationId);
  const [dimensions, setDimensions] = useState<VideoDimensions | null>(null);

  const handleCardClick = () => {
    navigate({
      to: ".",
      search: {
        operationIds: edit.operationId,
      },
    });
  };

  const handleDimensionsLoaded = ({ width, height }: VideoDimensions) => {
    setDimensions({ width, height });
    onDimensionsLoaded?.(edit.id, width, height);
  };

  return (
    <div className="flex flex-col items-center">
      <EditVideoFrame
        src={edit.assetUrl}
        variant="card"
        dimensions={dimensions}
        onDimensionsLoaded={handleDimensionsLoaded}
      />
      <div
        className={cn(
          "mt-3 text-center space-y-2",
          isLandscape(dimensions) ? "max-w-[280px]" : "max-w-[180px]"
        )}
      >
        <button
          className="text-xs font-mono text-primary hover:text-primary/80 underline decoration-dotted cursor-pointer transition-colors bg-transparent border-0 p-0 font-medium"
          onClick={handleCardClick}
          type="button"
        >
          {edit.id}
        </button>
        <p className="text-xs text-muted-foreground">{new Date(edit.createdAt).toLocaleString()}</p>
        <Button variant="outline" size="sm" onClick={copyLink} className="gap-1.5">
          {copied ? <CheckIcon className="size-3" /> : <LinkIcon className="size-3" />}
          {copied ? "Copied!" : "Copy Link"}
        </Button>
        <div className="flex flex-col items-center gap-1 text-xs text-muted-foreground">
          {edit.videoType && edit.videoType !== "VIDEO_TYPE_UNSPECIFIED" && (
            <Badge
              className={`text-[10px] px-1.5 py-0 ${VIDEO_TYPE_COLORS[edit.videoType]} text-white`}
            >
              {VIDEO_TYPE_LABELS[edit.videoType]}
            </Badge>
          )}
          <SurfaceBadge edit={edit} />
          <StudioVideoLink edit={edit} />
          <span className="truncate">
            <span className="font-medium">Op:</span> {edit.operationId}
          </span>
          <span className="truncate">
            <span className="font-medium">User:</span> {edit.userId}
          </span>
        </div>
      </div>
    </div>
  );
}

function FeaturedEditCard({ operationId }: { operationId: string }) {
  const {
    data: edit,
    isLoading,
    error,
  } = fetchAiEditLookupQuery.useQuery({
    pathParams: { editId: operationId },
  });

  if (isLoading) {
    return <div className="text-muted-foreground">Loading details...</div>;
  }

  if (error || !edit) {
    return (
      <div className="text-muted-foreground">
        Failed to load details. Error: {error?.message ?? "Not found"}
      </div>
    );
  }

  return <DetailEditCard edit={edit} />;
}

function useJsonFetch(url: string | null | undefined) {
  return useQuery({
    queryKey: ["json-fetch", url],
    queryFn: async () => {
      if (!url) {
        return null;
      }
      const res = await fetch(url);
      return res.json();
    },
    enabled: !!url,
  });
}

function V2ComparisonSection({
  comparison,
  v3VideoUrl,
}: {
  comparison: AiEditDetail["v2Comparison"];
  v3VideoUrl?: string | null;
}) {
  if (!comparison) {
    return null;
  }

  const winnerColor = match(comparison.comparisonWinner)
    .with("v3", () => "bg-green-600")
    .with("v2", () => "bg-blue-600")
    .otherwise(() => "bg-yellow-600");

  return (
    <Card className="mt-4">
      <CardHeader className="py-3">
        <CardTitle className="text-sm flex items-center gap-2">
          V2 vs V3 Comparison
          <Badge className={`${winnerColor} text-white`}>
            Winner: {comparison.comparisonWinner.toUpperCase()}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="py-3 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium mb-1">V2 Score</p>
            <p className="text-2xl font-bold">
              {comparison.v2ToolEvalScore?.toFixed(1) ?? "N/A"}/10
            </p>
            <p className="text-xs text-muted-foreground">{comparison.v2ShotCount} shots</p>
            {comparison.v2VideoUrl && (
              <video
                src={comparison.v2VideoUrl}
                controls
                className="max-w-[200px] rounded border mt-2"
              />
            )}
          </div>
          <div>
            <p className="text-xs font-medium mb-1">V3 Score</p>
            <p className="text-2xl font-bold">
              {comparison.v3ToolEvalScore?.toFixed(1) ?? "N/A"}/10
            </p>
            {v3VideoUrl && (
              <video src={v3VideoUrl} controls className="max-w-[200px] rounded border mt-2" />
            )}
          </div>
        </div>

        <div>
          <p className="text-xs font-medium mb-1">Reasoning</p>
          <p className="text-sm text-muted-foreground">{comparison.comparisonReasoning}</p>
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Confidence: {(comparison.comparisonConfidence * 100).toFixed(0)}%</span>
          {comparison.comparisonCreatedAt && (
            <>
              <span>•</span>
              <span>Compared: {new Date(comparison.comparisonCreatedAt).toLocaleString()}</span>
            </>
          )}
        </div>

        {comparison.v2Error && (
          <div className="text-red-500 text-sm">V2 Error: {comparison.v2Error}</div>
        )}
      </CardContent>
    </Card>
  );
}

const ANNOTATED_ATTEMPT_KINDS = {
  grid: {
    label: "Annotated Grid",
    imageAlt: "Annotated Asset Grid",
    verificationLabel: "Grid Verification",
    downloadName: "annotated-grid",
    annotatedUrl: (artifacts?: BboxAttemptArtifacts) => artifacts?.assetGridAnnotatedUrl,
    verificationUrl: (artifacts?: BboxAttemptArtifacts) => artifacts?.assetGridVerificationUrl,
  },
  reference: {
    label: "Annotated Ref",
    imageAlt: "Annotated Reference",
    verificationLabel: "Ref Verification",
    downloadName: "annotated-ref",
    annotatedUrl: (artifacts?: BboxAttemptArtifacts) => artifacts?.referenceAnnotatedUrl,
    verificationUrl: (artifacts?: BboxAttemptArtifacts) => artifacts?.referenceVerificationUrl,
  },
} as const;

/**
 * One bbox-attempt annotation column: an attempt picker, the selected attempt's annotated image
 * with a hover download, and that attempt's verification JSON. Renders nothing when no attempt
 * produced either artifact.
 */
function AnnotatedAttemptPanel({
  kind,
  attempts,
  selectedAttempt,
  onSelectAttempt,
}: {
  kind: keyof typeof ANNOTATED_ATTEMPT_KINDS;
  attempts: [string, BboxAttemptArtifacts][];
  selectedAttempt: string;
  onSelectAttempt: (attempt: string) => void;
}) {
  const { label, imageAlt, verificationLabel, downloadName, annotatedUrl, verificationUrl } =
    ANNOTATED_ATTEMPT_KINDS[kind];

  const annotated = attempts.filter(([, d]) => annotatedUrl(d) || verificationUrl(d));
  if (annotated.length === 0) {
    return null;
  }

  const selected = attempts.find(([num]) => num === selectedAttempt)?.[1];
  const selectedAnnotatedUrl = annotatedUrl(selected);
  const selectedVerificationUrl = verificationUrl(selected);

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <p className="text-xs text-muted-foreground">{label}</p>
        {attempts.length > 1 && annotated.length > 1 && (
          <div className="flex gap-1">
            {annotated.map(([num]) => (
              <button
                key={num}
                onClick={() => onSelectAttempt(num)}
                className={`px-1.5 py-0.5 text-[10px] rounded ${
                  selectedAttempt === num
                    ? "bg-primary text-primary-foreground"
                    : "bg-zinc-800 text-muted-foreground hover:bg-zinc-700"
                }`}
              >
                #{num}
              </button>
            ))}
          </div>
        )}
      </div>
      {selectedAnnotatedUrl && (
        <div className="relative group">
          <img
            src={selectedAnnotatedUrl}
            alt={imageAlt}
            className="h-56 rounded border border-zinc-700"
          />
          <button
            type="button"
            onClick={() =>
              downloadUrl(selectedAnnotatedUrl, `${downloadName}-${selectedAttempt}.png`)
            }
            className="absolute top-1 right-1 p-1 rounded bg-black/50 hover:bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            title="Download"
          >
            <DownloadIcon className="size-3" />
          </button>
        </div>
      )}
      {selectedVerificationUrl && (
        <JsonViewer url={selectedVerificationUrl} label={verificationLabel} />
      )}
    </div>
  );
}

function DetailEditCard({ edit }: { edit: AiEditDetail }) {
  const { copied, copyLink } = useCopyEditLink(edit.operationId);
  const [dimensions, setDimensions] = useState<VideoDimensions | null>(null);

  const motionClips = useMemo(
    () => (edit.motionClips ? Object.entries(edit.motionClips) : []),
    [edit.motionClips]
  );
  const [selectedClipIdx, setSelectedClipIdx] = useState(0);
  const selectedClip = motionClips[selectedClipIdx];
  const artifacts = selectedClip?.[1];

  const attempts = useMemo(
    () => (artifacts?.bboxAttempts ? Object.entries(artifacts.bboxAttempts) : []),
    [artifacts?.bboxAttempts]
  );
  const [gridAttempt, setGridAttempt] = useState(attempts[0]?.[0] ?? "");
  const [refAttempt, setRefAttempt] = useState(attempts[0]?.[0] ?? "");

  const { data: graphicJson } = useJsonFetch(artifacts?.graphicJsonUrl);
  const description = graphicJson?.description;

  useEffect(() => {
    if (attempts.length > 0) {
      if (!attempts.find(([k]) => k === gridAttempt)) {
        setGridAttempt(attempts[0][0]);
      }
      if (!attempts.find(([k]) => k === refAttempt)) {
        setRefAttempt(attempts[0][0]);
      }
    }
  }, [selectedClipIdx, attempts, gridAttempt, refAttempt]);

  return (
    <div className="w-full h-full flex flex-col items-center">
      <div className="flex items-center justify-center gap-2 mb-2 text-[10px] flex-wrap max-w-6xl">
        <span className="font-mono">{edit.id}</span>
        <span className="text-muted-foreground">•</span>
        <span className="text-muted-foreground">{new Date(edit.createdAt).toLocaleString()}</span>
        <span className="text-muted-foreground">•</span>
        <span className="text-muted-foreground">User:</span> {edit.userId}
        <SurfaceBadge edit={edit} />
        <StudioVideoLink edit={edit} />
        {edit.videoType && edit.videoType !== "VIDEO_TYPE_UNSPECIFIED" && (
          <Badge
            className={`text-[10px] px-1.5 py-0 ${VIDEO_TYPE_COLORS[edit.videoType]} text-white`}
          >
            {VIDEO_TYPE_LABELS[edit.videoType]}
          </Badge>
        )}
        {edit.videoStyleId && (
          <>
            <span className="text-muted-foreground">•</span>
            {edit.videoStyleName ?? edit.videoStyleId}
            {edit.videoStyleName && (
              <span className="text-muted-foreground"> ({edit.videoStyleId})</span>
            )}
          </>
        )}
        {edit.artifactDirUrl && (
          <>
            <span className="text-muted-foreground">•</span>
            <a
              href={edit.artifactDirUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Artifacts →
            </a>
          </>
        )}
        {motionClips.length > 1 && (
          <>
            <span className="text-muted-foreground">•</span>
            {motionClips.map(([clipId], idx) => (
              <button
                key={clipId}
                onClick={() => setSelectedClipIdx(idx)}
                className={`px-1.5 py-0.5 text-[10px] rounded font-mono ${
                  selectedClipIdx === idx
                    ? "bg-primary text-primary-foreground"
                    : "bg-zinc-800 text-muted-foreground hover:bg-zinc-700"
                }`}
              >
                {clipId}
              </button>
            ))}
          </>
        )}
      </div>

      <div
        className={`grid gap-6 flex-1 min-h-0 justify-center mx-auto max-w-6xl ${isLandscape(dimensions) ? "grid-cols-[400px_340px_1fr]" : "grid-cols-[240px_340px_1fr]"}`}
      >
        <div className="flex flex-col items-center">
          <EditVideoFrame
            src={edit.assetUrl}
            variant="detail"
            dimensions={dimensions}
            onDimensionsLoaded={setDimensions}
          />
          <div className="mt-3 text-center space-y-2">
            <Button variant="outline" size="sm" onClick={copyLink} className="gap-1.5">
              {copied ? <CheckIcon className="size-4" /> : <LinkIcon className="size-4" />}
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>
        </div>

        <div className="space-y-3 overflow-y-auto">
          {description && (
            <div className="bg-zinc-100 rounded-lg p-3 border border-zinc-300">
              <p className="text-sm leading-relaxed text-zinc-800">{description}</p>
            </div>
          )}

          <div className="flex gap-3">
            {artifacts?.referenceAssetUrl && (
              <ImageWithDownload
                src={artifacts.referenceAssetUrl}
                alt="Reference"
                label="Reference"
                className="h-44 rounded border border-zinc-700"
              />
            )}
            {artifacts?.assetGridUrl && (
              <ImageWithDownload
                src={artifacts.assetGridUrl}
                alt="Asset Grid"
                label="Asset Grid"
                className="h-44 rounded border border-zinc-700"
              />
            )}
          </div>

          <div className="space-y-1">
            {artifacts?.assetGridVerificationUrl && (
              <JsonViewer
                url={artifacts.assetGridVerificationUrl}
                label="Asset Grid Verification"
              />
            )}
            {artifacts?.graphicJsonUrl && (
              <JsonViewer url={artifacts.graphicJsonUrl} label="Graphic JSON" />
            )}
            {edit.timelineJsonUrl && (
              <JsonViewer url={edit.timelineJsonUrl} label="Timeline JSON" />
            )}
          </div>
        </div>

        <div className="space-y-3 overflow-y-auto">
          {attempts.length > 0 ? (
            <div className="flex gap-4">
              <AnnotatedAttemptPanel
                kind="grid"
                attempts={attempts}
                selectedAttempt={gridAttempt}
                onSelectAttempt={setGridAttempt}
              />
              <AnnotatedAttemptPanel
                kind="reference"
                attempts={attempts}
                selectedAttempt={refAttempt}
                onSelectAttempt={setRefAttempt}
              />
            </div>
          ) : (
            <p className="text-muted-foreground">No bbox attempts</p>
          )}
        </div>
      </div>

      {/* V2 Comparison Section */}
      {edit.v2Comparison && (
        <div className="w-full max-w-6xl mx-auto mt-4">
          <V2ComparisonSection comparison={edit.v2Comparison} v3VideoUrl={edit.assetUrl} />
        </div>
      )}
    </div>
  );
}

const VIDEO_TYPE_OPTIONS: { value: VideoType | "all"; label: string }[] = [
  { value: "all", label: "All Types" },
  { value: "VIDEO_TYPE_TALKING_HEAD", label: VIDEO_TYPE_LABELS.VIDEO_TYPE_TALKING_HEAD },
  { value: "VIDEO_TYPE_VOICEOVER", label: VIDEO_TYPE_LABELS.VIDEO_TYPE_VOICEOVER },
  { value: "VIDEO_TYPE_REAL_MIRAGE", label: VIDEO_TYPE_LABELS.VIDEO_TYPE_REAL_MIRAGE },
];

const VIDEO_TYPE_COLORS: Record<VideoType, string> = {
  VIDEO_TYPE_UNSPECIFIED: "bg-zinc-500",
  VIDEO_TYPE_TALKING_HEAD: "bg-[#74568C]",
  VIDEO_TYPE_VOICEOVER: "bg-[#447096]",
  VIDEO_TYPE_REAL_MIRAGE: "bg-[#AD4818]",
};

function RouteComponent() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const {
    operationIds: initialOperationIds,
    userIds: initialUserIds,
    orgIds: initialOrgIds,
    videoTypes: initialVideoTypes,
    isHorizontal: initialIsHorizontal,
    surface: initialSurface,
  } = search;

  const [operationIdsFilter, setOperationIdsFilter] = useState(initialOperationIds ?? "");
  const [userIdsFilter, setUserIdsFilter] = useState(initialUserIds ?? "");
  const [orgIdsFilter, setOrgIdsFilter] = useState(initialOrgIds ?? "");
  const [selectedVideoType, setSelectedVideoType] = useState<VideoType | "all">(() =>
    parseVideoTypeParam(initialVideoTypes)
  );
  const [selectedSurface, setSelectedSurface] = useState<EditSurface | "all">(
    initialSurface ?? "all"
  );
  const [hideTestUsers, setHideTestUsers] = useState(false);
  const [horizontalOnly, setHorizontalOnly] = useState(initialIsHorizontal ?? false);
  const [videoDimensions, setVideoDimensions] = useState<
    Map<string, { width: number; height: number }>
  >(() => new Map());
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>(() =>
    filtersFromSearch(search)
  );
  const [globalLinkCopied, setGlobalLinkCopied] = useState(false);

  const loadMoreRef = useRef<HTMLDivElement>(null);

  const queryFilters = Object.keys(appliedFilters).length > 0 ? appliedFilters : undefined;

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    fetchAiEditsFeedQuery.useInfiniteQuery({
      params: {
        filters: queryFilters,
      },
    });

  const handleVideoDimensionsLoaded = (editId: string, width: number, height: number) => {
    setVideoDimensions((prev) => {
      const next = new Map(prev);
      next.set(editId, { width, height });
      return next;
    });
  };

  const edits = useMemo(() => {
    if (!data?.pages) {
      return [];
    }
    let allEdits = data.pages.flatMap((page) => page.edits.page);
    if (hideTestUsers) {
      allEdits = allEdits.filter(
        (edit) => !edit.userId.toLowerCase().startsWith("test") && edit.userId !== "dev-user-id"
      );
    }
    return allEdits;
  }, [data?.pages, hideTestUsers]);

  const filteredEdits = useMemo(() => {
    if (!horizontalOnly) {
      return edits;
    }
    return edits.filter((edit) => {
      const dims = videoDimensions.get(edit.id);
      if (!dims) {
        return true;
      }
      return dims.width > dims.height;
    });
  }, [edits, horizontalOnly, videoDimensions]);

  const featuredEdit = useMemo(() => {
    if (appliedFilters.operationIds?.length === 1 && filteredEdits.length > 0) {
      return filteredEdits.find((edit) => edit.operationId === appliedFilters.operationIds![0]);
    }
    return null;
  }, [appliedFilters.operationIds, filteredEdits]);

  const gridEdits = useMemo(() => {
    if (!featuredEdit) {
      return filteredEdits;
    }
    return filteredEdits.filter((edit) => edit.id !== featuredEdit.id);
  }, [filteredEdits, featuredEdit]);

  const copyGlobalLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    await navigator.clipboard.writeText(url);
    setGlobalLinkCopied(true);
    setTimeout(() => setGlobalLinkCopied(false), 2000);
  };

  const applyFilters = (overrides?: {
    videoType?: VideoType | "all";
    surface?: EditSurface | "all";
  }) => {
    setAppliedFilters(
      buildFilters({
        operationIds: operationIdsFilter,
        userIds: userIdsFilter,
        orgIds: orgIdsFilter,
        videoType: overrides?.videoType ?? selectedVideoType,
        surface: overrides?.surface ?? selectedSurface,
      })
    );
  };

  const handleVideoTypeChange = (value: VideoType | "all") => {
    setSelectedVideoType(value);
    applyFilters({ videoType: value });
  };

  const handleSurfaceChange = (value: EditSurface | "all") => {
    setSelectedSurface(value);
    applyFilters({ surface: value });
  };

  const clearFilters = () => {
    setOperationIdsFilter("");
    setUserIdsFilter("");
    setOrgIdsFilter("");
    setSelectedVideoType("all");
    setSelectedSurface("all");
    setAppliedFilters({});
    setHorizontalOnly(false);
  };

  // Sync from the URL (back/forward, links from other pages, the /studio-edits redirect).
  useOnArrayChangeEffect(
    [initialOperationIds, initialUserIds, initialOrgIds, initialVideoTypes, initialSurface],
    () => {
      setAppliedFilters(
        filtersFromSearch({
          operationIds: initialOperationIds,
          userIds: initialUserIds,
          orgIds: initialOrgIds,
          videoTypes: initialVideoTypes,
          surface: initialSurface,
        })
      );
      setOperationIdsFilter(initialOperationIds ?? "");
      setUserIdsFilter(initialUserIds ?? "");
      setOrgIdsFilter(initialOrgIds ?? "");
      setSelectedVideoType(parseVideoTypeParam(initialVideoTypes));
      setSelectedSurface(initialSurface ?? "all");
    }
  );

  useEffect(() => {
    navigate({
      to: ".",
      search: {
        operationIds: appliedFilters.operationIds?.join(",") || undefined,
        userIds: appliedFilters.userIds?.join(",") || undefined,
        orgIds: appliedFilters.orgIds?.join(",") || undefined,
        videoTypes: appliedFilters.videoTypes?.[0] || undefined,
        surface: appliedFilters.surface,
        isHorizontal: horizontalOnly || undefined,
      },
      replace: true,
    });
  }, [
    appliedFilters.operationIds,
    appliedFilters.userIds,
    appliedFilters.orgIds,
    appliedFilters.videoTypes,
    appliedFilters.surface,
    horizontalOnly,
    navigate,
  ]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AI Edits</h1>
          <p className="text-muted-foreground">
            AI Edit V3 runs from the Captions app and the Mirage Studio website
          </p>
        </div>
        <Button variant="outline" onClick={copyGlobalLink} className="gap-2">
          {globalLinkCopied ? <CheckIcon className="size-4" /> : <LinkIcon className="size-4" />}
          {globalLinkCopied ? "Copied!" : "Copy Page Link"}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <label htmlFor="operation-ids-filter" className="text-sm font-medium">
                Operation IDs
              </label>
              <Input
                id="operation-ids-filter"
                placeholder="e.g. op1, op2"
                value={operationIdsFilter}
                onChange={(e) => setOperationIdsFilter(e.target.value)}
                className="w-48"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="user-ids-filter" className="text-sm font-medium">
                User IDs
              </label>
              <Input
                id="user-ids-filter"
                placeholder="e.g. user-1, user-2"
                value={userIdsFilter}
                onChange={(e) => setUserIdsFilter(e.target.value)}
                className="w-48"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="org-ids-filter" className="text-sm font-medium">
                Org IDs <span className="text-muted-foreground font-normal">(website only)</span>
              </label>
              <Input
                id="org-ids-filter"
                placeholder="e.g. org-1, org-2"
                value={orgIdsFilter}
                onChange={(e) => setOrgIdsFilter(e.target.value)}
                className="w-48"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="surface-filter" className="text-sm font-medium">
                Surface
              </label>
              <Select
                value={selectedSurface}
                onValueChange={(v) => handleSurfaceChange(v as EditSurface | "all")}
              >
                <SelectTrigger id="surface-filter" className="w-40">
                  <SelectValue placeholder="All Surfaces" />
                </SelectTrigger>
                <SelectContent>
                  {SURFACE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label htmlFor="video-type-filter" className="text-sm font-medium">
                Video Type
              </label>
              <Select
                value={selectedVideoType}
                onValueChange={(v) => handleVideoTypeChange(v as VideoType | "all")}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  {VIDEO_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="hide-test-users-toggle"
                checked={hideTestUsers}
                onCheckedChange={setHideTestUsers}
              />
              <label
                htmlFor="hide-test-users-toggle"
                className="text-sm font-medium cursor-pointer"
              >
                Hide Test Users
              </label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="horizontal-only-toggle"
                checked={horizontalOnly}
                onCheckedChange={setHorizontalOnly}
              />
              <label
                htmlFor="horizontal-only-toggle"
                className="text-sm font-medium cursor-pointer"
              >
                Horizontal Only
              </label>
            </div>
            <Button onClick={() => applyFilters()}>Apply</Button>
            <Button variant="outline" onClick={clearFilters}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {featuredEdit && (
        <Card>
          <CardHeader>
            <CardTitle>Featured Edit</CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center py-4 px-4">
            <FeaturedEditCard operationId={featuredEdit.operationId} />
          </CardContent>
        </Card>
      )}

      {!featuredEdit && (
        <div>
          {isLoading && (
            <div className="text-center py-12 text-muted-foreground">Loading edits...</div>
          )}

          {error && (
            <div className="text-center py-12 text-red-500">
              Error loading edits: {error.message}
            </div>
          )}

          {!isLoading && !error && edits.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              No edits found. Try adjusting your filters.
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {gridEdits.map((edit) => (
              <EditCard
                key={edit.id}
                edit={edit}
                onDimensionsLoaded={handleVideoDimensionsLoaded}
              />
            ))}
          </div>

          <div ref={loadMoreRef} className="h-10">
            {isFetchingNextPage && (
              <div className="text-center py-4 text-muted-foreground">Loading more...</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
